import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import ArrowButton from "basicComponents/ArrowButton/ArrowButton";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: "flex",
    alignItems: "center",
  },
  page: {
    cursor: "pointer",
    display: "inline-block",
    width: "40px",
    height: "40px",
    textAlign: "center",
    lineHeight: "40px",

    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.10)",
    },

    "&.active": {
      backgroundColor: "rgba(0,0,0,0.10)",
    },
  },
}));

function SimplePagination({ total = 0, currentPage = 0, limit = 0, onChange }) {
  const classes = useStyles();

  const numberOfPages = useMemo(
    () => Math.ceil((total > 0 ? total : 1) / limit),
    [limit, total],
  );

  const nextAvailable = useMemo(() => currentPage + 1 < numberOfPages, [
    currentPage,
    numberOfPages,
  ]);

  const prevAvailable = useMemo(() => currentPage > 0, [currentPage]);

  const onNext = useCallback(
    () => {
      onChange(currentPage + 1);
    },
    [currentPage, onChange],
  );

  const onPrev = useCallback(
    () => {
      onChange(currentPage - 1);
    },
    [currentPage, onChange],
  );

  const handleChange = useCallback(
    page => () => {
      onChange(page - 1);
    },
    [onChange],
  );

  const renderPages = useCallback(
    () => {
      const pages = [];

      let i = 1;

      if (numberOfPages > 9 && currentPage > 4) {
        i = currentPage - 3;

        if (i + 8 > numberOfPages) {
          i = numberOfPages - 8;
        }
      }

      const lastPage = Math.min(i + 8, numberOfPages);

      for (i; i <= lastPage; i++) {
        pages.push(
          <span
            key={`pagination_page_${i}`}
            onClick={handleChange(i)}
            className={clsx(classes.page, {
              active: i === currentPage + 1,
            })}
          >
            {i}
          </span>,
        );
      }

      return pages;
    },
    [classes.page, currentPage, handleChange, numberOfPages],
  );

  return (
    <div className={classes.wrapper}>
      <ArrowButton
        left
        onClick={onPrev}
        disabled={!prevAvailable}
        id="previous-pagination-page"
      />
      <div>{renderPages()}</div>
      <ArrowButton
        onClick={onNext}
        disabled={!nextAvailable}
        id="next-pagination-page"
      />
    </div>
  );
}

SimplePagination.propTypes = {
  total: PropTypes.number,
  currentPage: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  limit: PropTypes.number,
};

export default SimplePagination;
